import {
    PaginatedBusinessEstimatesWithAccountInfo,
    PaginatedLogisticsEstimatesWithAccountInfo,
} from '@lune-fe/dapi-models'
import { PaginationKeys, useErrorSnackbar, usePaginationState } from '@lune-fe/lune-components-lib'
import { ButtonGroupNew, Pagination } from '@lune-fe/lune-ui-lib'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined'
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined'
import Box from '@mui/material/Box'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Range } from 'react-date-range'
import { useSearchParams } from 'react-router-dom'

import { PredefinedRange } from 'components/DateRangeFilterNew'
import FiltersBoxNew from 'components/FiltersBoxNew'
import FiltersWithQueryParamsNew from 'components/FiltersWithQueryParamsNew'
import WhiteBoxContainer from 'components/WhiteBoxContainer'
import { getAccountsById, getBusinessEstimates, getLogisticsEstimates } from 'endpoints/dapi'
import { SnackbarMessages } from 'SnackbarMessages'
import {
    BusinessEmissionCalculationTable,
    EmissionCalculationFilters,
    EstimateTypeEnum,
    EstimateTypeName,
    LogisticsEmissionCalculationTable,
    LogisticsEstimatesFilter,
    SpendEstimatesFilter,
    SpendEstimateType,
} from 'views/CalculateEmissions'
import ActiveFiltersChips from 'views/CalculateEmissions/Tables/ActiveFiltersChips'

type LoadLogisticsEstimationsProps = Omit<LogisticsEstimatesFilter, 'calculationType'>
type LoadEstimationsProps = Omit<SpendEstimatesFilter, 'calculationType'>

interface EmissionCalculationsProps {
    onFiltersChange?: (filters: EmissionCalculationFilters) => void
}

const CALCULATION_TYPE_STORAGE_KEY = 'emission_calculation_type'

const EmissionCalculations = ({ onFiltersChange }: EmissionCalculationsProps) => {
    const ITEMS_PER_PAGE = 50
    const { enqueueSnackbar: snackbar } = useErrorSnackbar()
    const { restoreAndClearPaginationState, clearPaginationState } = usePaginationState()
    const [searchParams, setSearchParams] = useSearchParams()

    const [emissionCalculationTypeSelected, setEmissionCalculationTypeSelected] = useState<
        'Spend' | 'Logistics'
    >('Logistics')

    // Common filters
    const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([])
    const [dateRange, setDateRange] = useState<Range | undefined>({
        startDate: moment().subtract(30, 'days').toDate(),
        endDate: moment().toDate(),
    })

    // Spend-specific filters
    const [selectedTypes, setSelectedTypes] = useState<SpendEstimateType[]>([])

    // Logistics-specific filters
    const [selectedSources, setSelectedSources] = useState<string[]>([])
    const [selectedShipmentStatuses, setSelectedShipmentStatuses] = useState<string[]>([])

    // Account name lookup for display in chips
    const [accountNamesMap, setAccountNamesMap] = useState<Record<string, string>>({})

    // Fetch account names for selected accounts (for chip display)
    useEffect(() => {
        if (selectedAccountIds.length > 0) {
            getAccountsById({ accountId: selectedAccountIds }).then((accounts) => {
                const namesMap: Record<string, string> = Object.fromEntries(
                    accounts.map((acc) => [acc.id, acc.name]),
                )
                setAccountNamesMap(namesMap)
            })
        } else {
            setAccountNamesMap({})
        }
    }, [selectedAccountIds])

    const [loading, setLoading] = useState(true)
    const [currentPage, setCurrentPage] = useState(1)

    const [paginatedBusinessEstimates, setPaginatedBusinessEstimates] =
        useState<PaginatedBusinessEstimatesWithAccountInfo>()
    const [paginatedLogisticsEstimates, setPaginatedLogisticsEstimates] =
        useState<PaginatedLogisticsEstimatesWithAccountInfo>()

    const [queryParams, setQueryParams] = useState<string>()
    const pageHistoryRef = useRef<(string | undefined)[]>([undefined])
    const [loadingInitialState, setLoadingInitialState] = useState(true)
    const [filtersInitialized, setFiltersInitialized] = useState(false)
    const hasRestoredPaginationRef = useRef(false)
    const restoredPageRef = useRef<number>(1)
    const previousFiltersRef = useRef<{
        dateRange: Range | undefined
        types: SpendEstimateType[]
        accountIds: string[]
        sources: string[]
        shipmentStatuses: string[]
        calculationType: 'Spend' | 'Logistics'
    }>({
        dateRange: { startDate: undefined, endDate: undefined },
        types: [],
        accountIds: [],
        sources: [],
        shipmentStatuses: [],
        calculationType: 'Spend',
    })

    // Initialize calculation type from URL, localStorage, and restore pagination on mount
    useEffect(() => {
        const typeFromUrl = searchParams.get('calculation_type')
        const typeFromStorage = localStorage.getItem(CALCULATION_TYPE_STORAGE_KEY)
        let calculationType: 'Spend' | 'Logistics' = 'Logistics'

        // Priority: URL > localStorage > default
        if (typeFromUrl === 'Logistics' || typeFromUrl === 'Spend') {
            calculationType = typeFromUrl
            setEmissionCalculationTypeSelected(typeFromUrl)
            // Update localStorage to match URL
            localStorage.setItem(CALCULATION_TYPE_STORAGE_KEY, typeFromUrl)
        } else if (typeFromStorage === 'Logistics' || typeFromStorage === 'Spend') {
            // Restore from localStorage if URL doesn't have it
            calculationType = typeFromStorage
            setEmissionCalculationTypeSelected(typeFromStorage)
            setSearchParams(
                (prev) => {
                    const newParams = new URLSearchParams(prev)
                    newParams.set('calculation_type', typeFromStorage)
                    return newParams
                },
                { replace: true },
            )
        } else {
            // Set default in URL and localStorage
            localStorage.setItem(CALCULATION_TYPE_STORAGE_KEY, 'Spend')
            setSearchParams(
                (prev) => {
                    const newParams = new URLSearchParams(prev)
                    newParams.set('calculation_type', 'Spend')
                    return newParams
                },
                { replace: true },
            )
        }

        // Restore pagination state based on the calculation type
        const paginationKey =
            calculationType === 'Spend'
                ? PaginationKeys.EMISSION_CALCULATION_BUSINESS
                : PaginationKeys.EMISSION_CALCULATION_LOGISTICS
        const savedState = restoreAndClearPaginationState(paginationKey)
        if (savedState) {
            setCurrentPage(savedState.page)
            pageHistoryRef.current = savedState.cursorHistory
            hasRestoredPaginationRef.current = true
            restoredPageRef.current = savedState.page
        } else {
            restoredPageRef.current = 1
        }
        setLoadingInitialState(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Sync calculation type changes to URL and localStorage
    // Track the previous type to detect actual changes (not just re-renders)
    const previousTypeRef = useRef<'Spend' | 'Logistics'>()
    useEffect(() => {
        // Skip if this is the first time seeing this value (initialization)
        if (previousTypeRef.current === undefined) {
            previousTypeRef.current = emissionCalculationTypeSelected
            return
        }

        // Only run if the type actually changed
        if (previousTypeRef.current === emissionCalculationTypeSelected) {
            return
        }

        previousTypeRef.current = emissionCalculationTypeSelected

        // User manually switched types - reset to page 1
        localStorage.setItem(CALCULATION_TYPE_STORAGE_KEY, emissionCalculationTypeSelected)
        setSearchParams(
            (prev) => {
                const newParams = new URLSearchParams(prev)
                newParams.set('calculation_type', emissionCalculationTypeSelected)
                return newParams
            },
            { replace: true },
        )
        setCurrentPage(1)
        pageHistoryRef.current = [undefined]
        restoredPageRef.current = 1
    }, [emissionCalculationTypeSelected, setSearchParams])

    const loadBusinessEstimates = useCallback(
        async (
            { types, dateRange, selectedAccountIds }: LoadEstimationsProps,
            pageNumber: number,
        ) => {
            try {
                setLoading(true)
                const cursor = pageHistoryRef.current[pageNumber - 1]
                const paginatedResult = await getBusinessEstimates({
                    limit: ITEMS_PER_PAGE,
                    after: cursor,
                    accountId: selectedAccountIds,
                    from: dateRange?.startDate,
                    through: dateRange?.endDate,
                    type: types,
                })
                setPaginatedBusinessEstimates(paginatedResult)
                if (paginatedResult.hasMore && paginatedResult.nextCursor) {
                    const nextCursor = paginatedResult.nextCursor
                    const newHistory = [...pageHistoryRef.current]
                    newHistory[pageNumber] = nextCursor
                    pageHistoryRef.current = newHistory
                }
                setLoading(false)
            } catch (error) {
                setPaginatedBusinessEstimates({
                    hasMore: false,
                    nextCursor: null,
                    data: [],
                })
                snackbar(SnackbarMessages.GENERIC_ERROR, { error })
                setLoading(false)
            }
        },
        [snackbar],
    )

    const loadLogisticsEstimates = useCallback(
        async (
            {
                sources,
                shipmentStatuses,
                dateRange,
                selectedAccountIds,
            }: LoadLogisticsEstimationsProps,
            pageNumber: number,
        ) => {
            setLoading(true)
            const cursor = pageHistoryRef.current[pageNumber - 1]
            // Convert shipment statuses to isShipment boolean if needed
            // "shipment" maps to isShipment: true, "pre-shipment" maps to isShipment: false
            const isShipment =
                shipmentStatuses.length === 1 ? shipmentStatuses[0] === 'shipment' : undefined

            // Expand "import" into individual import types
            const expandedSources = sources.flatMap((source) => {
                if (source === 'import') {
                    return ['csv', 'ecotransit_csv', 'ecotransit', 'rfq']
                }
                return source
            })

            const result = await getLogisticsEstimates({
                limit: ITEMS_PER_PAGE,
                after: cursor,
                accountId: selectedAccountIds,
                from: dateRange?.startDate,
                through: dateRange?.endDate,
                source: expandedSources.length > 0 ? expandedSources : undefined,
                isShipment,
            })

            if (result.isOk()) {
                const paginatedResult = result.value
                setPaginatedLogisticsEstimates(paginatedResult)
                if (paginatedResult.hasMore && paginatedResult.nextCursor) {
                    const nextCursor = paginatedResult.nextCursor
                    const newHistory = [...pageHistoryRef.current]
                    newHistory[pageNumber] = nextCursor
                    pageHistoryRef.current = newHistory
                }
            } else {
                setPaginatedLogisticsEstimates({
                    hasMore: false,
                    nextCursor: null,
                    data: [],
                })
                snackbar(SnackbarMessages.GENERIC_ERROR, { error: result.error })
            }
            setLoading(false)
        },
        [snackbar],
    )

    const handlePageChange = (page: number) => {
        setCurrentPage(page)
        if (emissionCalculationTypeSelected === 'Spend') {
            loadBusinessEstimates(
                {
                    selectedAccountIds,
                    types: selectedTypes,
                    dateRange,
                },
                page,
            )
        } else {
            loadLogisticsEstimates(
                {
                    selectedAccountIds,
                    sources: selectedSources,
                    shipmentStatuses: selectedShipmentStatuses,
                    dateRange,
                },
                page,
            )
        }
    }

    // Notify parent component of filter changes
    useEffect(() => {
        if (onFiltersChange && filtersInitialized) {
            onFiltersChange({
                calculationType: emissionCalculationTypeSelected,
                selectedAccountIds,
                dateRange,
                types: selectedTypes,
                sources: selectedSources,
                shipmentStatuses: selectedShipmentStatuses,
            })
        }
    }, [
        onFiltersChange,
        emissionCalculationTypeSelected,
        selectedAccountIds,
        dateRange,
        selectedTypes,
        selectedSources,
        selectedShipmentStatuses,
        filtersInitialized,
    ])

    useEffect(
        // eslint-disable-next-line complexity
        () => {
            // Wait for both: component initialization AND filters initialization from URL
            if (!loadingInitialState && filtersInitialized) {
                // Check if this is initial load or a filter change
                const isInitialLoad =
                    previousFiltersRef.current.dateRange?.startDate === undefined &&
                    previousFiltersRef.current.dateRange?.endDate === undefined

                const calculationTypeChanged =
                    previousFiltersRef.current.calculationType !== emissionCalculationTypeSelected

                const commonFiltersChanged =
                    previousFiltersRef.current.dateRange?.startDate !== dateRange?.startDate ||
                    previousFiltersRef.current.dateRange?.endDate !== dateRange?.endDate ||
                    JSON.stringify(previousFiltersRef.current.accountIds) !==
                        JSON.stringify(selectedAccountIds)

                const spendFiltersChanged =
                    emissionCalculationTypeSelected === 'Spend' &&
                    JSON.stringify(previousFiltersRef.current.types) !==
                        JSON.stringify(selectedTypes)

                const logisticsFiltersChanged =
                    emissionCalculationTypeSelected === 'Logistics' &&
                    (JSON.stringify(previousFiltersRef.current.sources) !==
                        JSON.stringify(selectedSources) ||
                        JSON.stringify(previousFiltersRef.current.shipmentStatuses) !==
                            JSON.stringify(selectedShipmentStatuses))

                const filtersChanged =
                    calculationTypeChanged ||
                    commonFiltersChanged ||
                    spendFiltersChanged ||
                    logisticsFiltersChanged

                if (isInitialLoad || filtersChanged) {
                    // Reset pagination on filter change (but not on initial load)
                    if (!isInitialLoad) {
                        const paginationKey =
                            emissionCalculationTypeSelected === 'Spend'
                                ? PaginationKeys.EMISSION_CALCULATION_BUSINESS
                                : PaginationKeys.EMISSION_CALCULATION_LOGISTICS
                        clearPaginationState(paginationKey)
                        setCurrentPage(1)
                        pageHistoryRef.current = [undefined]
                    }

                    // Update the ref to track current filter state
                    previousFiltersRef.current = {
                        dateRange,
                        types: selectedTypes,
                        accountIds: selectedAccountIds,
                        sources: selectedSources,
                        shipmentStatuses: selectedShipmentStatuses,
                        calculationType: emissionCalculationTypeSelected,
                    }

                    // Determine which page to load
                    let targetPage = 1
                    if (isInitialLoad) {
                        // For initial load, use restored page from ref (this avoids state timing issues)
                        targetPage = restoredPageRef.current
                        // Ensure currentPage state is in sync
                        if (currentPage !== targetPage) {
                            setCurrentPage(targetPage)
                        }
                    }
                    // Load the appropriate data based on the selected type
                    if (emissionCalculationTypeSelected === 'Spend') {
                        loadBusinessEstimates(
                            {
                                selectedAccountIds,
                                types: selectedTypes,
                                dateRange,
                            },
                            targetPage,
                        )
                    } else {
                        loadLogisticsEstimates(
                            {
                                selectedAccountIds,
                                sources: selectedSources,
                                shipmentStatuses: selectedShipmentStatuses,
                                dateRange,
                            },
                            targetPage,
                        )
                    }
                }
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [
            selectedAccountIds,
            loadingInitialState,
            dateRange,
            selectedTypes,
            selectedSources,
            selectedShipmentStatuses,
            filtersInitialized,
            emissionCalculationTypeSelected,
        ],
    )

    return (
        <>
            <WhiteBoxContainer
                sx={{
                    overflow: 'hidden',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <FiltersWithQueryParamsNew<string | EstimateTypeEnum>
                    filters={
                        emissionCalculationTypeSelected === 'Spend'
                            ? [
                                  {
                                      queryParamKey: 'account_id',
                                      selectedValues: selectedAccountIds,
                                      setSelectedValues: (values) =>
                                          setSelectedAccountIds(values as string[]),
                                  },
                                  {
                                      queryParamKey: 'type',
                                      selectedValues: selectedTypes,
                                      setSelectedValues: (values) =>
                                          setSelectedTypes(values as SpendEstimateType[]),
                                  },
                              ]
                            : [
                                  {
                                      queryParamKey: 'account_id',
                                      selectedValues: selectedAccountIds,
                                      setSelectedValues: setSelectedAccountIds,
                                  },
                                  {
                                      queryParamKey: 'source',
                                      selectedValues: selectedSources,
                                      setSelectedValues: setSelectedSources,
                                  },
                                  {
                                      queryParamKey: 'shipment_status',
                                      selectedValues: selectedShipmentStatuses,
                                      setSelectedValues: setSelectedShipmentStatuses,
                                  },
                              ]
                    }
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                    setQueryParams={setQueryParams}
                    onFiltersInitialized={() => setFiltersInitialized(true)}
                />
                <Box
                    sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: 2,
                        }}
                    >
                        <ButtonGroupNew
                            value={emissionCalculationTypeSelected}
                            items={[
                                {
                                    value: 'Logistics',
                                    label: 'Logistics',
                                },
                                {
                                    value: 'Spend',
                                    label: 'Spend',
                                },
                            ]}
                            onChange={(type) =>
                                setEmissionCalculationTypeSelected(type as 'Spend' | 'Logistics')
                            }
                        />
                        <FiltersBoxNew<string | EstimateTypeEnum>
                            preselectedDateRange={dateRange}
                            setDateRange={setDateRange}
                            preselectedAccountIds={selectedAccountIds}
                            setSelectedAccountIds={setSelectedAccountIds}
                            defaultDateRange={PredefinedRange.THIRTY_DAYS}
                            filters={
                                emissionCalculationTypeSelected === 'Spend'
                                    ? [
                                          {
                                              filterName: 'type',
                                              filterIcon: <WidgetsOutlinedIcon />,
                                              options: [
                                                  EstimateTypeEnum.TRANSACTION,
                                                  EstimateTypeEnum.TRANSACTION_DOCUMENT,
                                                  EstimateTypeEnum.PASSENGER_TRANSPORT,
                                                  EstimateTypeEnum.FLIGHT,
                                                  EstimateTypeEnum.EMISSION_FACTOR,
                                              ],
                                              preselected: selectedTypes,
                                              setSelectedOptions: (selection) =>
                                                  setSelectedTypes(
                                                      selection as SpendEstimateType[],
                                                  ),
                                              transformLabel: (label) =>
                                                  EstimateTypeName(label as EstimateTypeEnum),
                                          },
                                      ]
                                    : [
                                          {
                                              filterName: 'source',
                                              filterIcon: <SourceOutlinedIcon />,
                                              options: ['api', 'dashboard', 'import'],
                                              preselected: selectedSources,
                                              setSelectedOptions: setSelectedSources,
                                              transformLabel: (label: string) =>
                                                  label.charAt(0).toUpperCase() + label.slice(1),
                                          },
                                          {
                                              filterName: 'shipment status',
                                              filterIcon: <LocalShippingOutlinedIcon />,
                                              options: ['shipment', 'pre-shipment'],
                                              preselected: selectedShipmentStatuses,
                                              setSelectedOptions: setSelectedShipmentStatuses,
                                              transformLabel: (label: string) =>
                                                  label.charAt(0).toUpperCase() + label.slice(1),
                                          },
                                      ]
                            }
                        />
                    </Box>
                    {emissionCalculationTypeSelected === 'Spend' ? (
                        <>
                            {paginatedBusinessEstimates &&
                                paginatedBusinessEstimates.data.length > 0 && (
                                    <Pagination
                                        currentPage={currentPage}
                                        itemsPerPage={ITEMS_PER_PAGE}
                                        currentPageItemCount={
                                            paginatedBusinessEstimates.data.length
                                        }
                                        onPageChange={handlePageChange}
                                        defaultHasNext={paginatedBusinessEstimates.hasMore}
                                        loading={loading}
                                    />
                                )}
                        </>
                    ) : (
                        <>
                            {paginatedLogisticsEstimates &&
                                paginatedLogisticsEstimates.data.length > 0 && (
                                    <Pagination
                                        currentPage={currentPage}
                                        itemsPerPage={ITEMS_PER_PAGE}
                                        currentPageItemCount={
                                            paginatedLogisticsEstimates.data.length
                                        }
                                        onPageChange={handlePageChange}
                                        defaultHasNext={paginatedLogisticsEstimates.hasMore}
                                        loading={loading}
                                    />
                                )}
                        </>
                    )}
                </Box>
                {emissionCalculationTypeSelected === 'Spend' ? (
                    <>
                        <ActiveFiltersChips<string | EstimateTypeEnum>
                            filters={[
                                {
                                    filterName: 'account',
                                    selectedValues: selectedAccountIds.filter(
                                        (id) => accountNamesMap[id],
                                    ),
                                    setSelectedValues: (ids) =>
                                        setSelectedAccountIds(ids as string[]),
                                    transformLabel: (id) => accountNamesMap[id],
                                },
                                {
                                    filterName: 'type',
                                    selectedValues: selectedTypes,
                                    setSelectedValues: (selection) =>
                                        setSelectedTypes(selection as SpendEstimateType[]),
                                    transformLabel: (label) =>
                                        EstimateTypeName(label as EstimateTypeEnum),
                                },
                            ]}
                        />
                        <BusinessEmissionCalculationTable
                            loading={loading}
                            queryParams={queryParams}
                            currentPage={currentPage}
                            pageHistoryRef={pageHistoryRef}
                            paginatedEstimates={paginatedBusinessEstimates}
                        />
                    </>
                ) : (
                    <>
                        <ActiveFiltersChips<string>
                            filters={[
                                {
                                    filterName: 'account',
                                    selectedValues: selectedAccountIds.filter(
                                        (id) => accountNamesMap[id],
                                    ),
                                    setSelectedValues: setSelectedAccountIds,
                                    transformLabel: (id: string) => accountNamesMap[id],
                                },
                                {
                                    filterName: 'source',
                                    selectedValues: selectedSources,
                                    setSelectedValues: setSelectedSources,
                                    transformLabel: (label: string) =>
                                        label.charAt(0).toUpperCase() + label.slice(1),
                                },
                                {
                                    filterName: 'shipment status',
                                    selectedValues: selectedShipmentStatuses,
                                    setSelectedValues: setSelectedShipmentStatuses,
                                    transformLabel: (label: string) =>
                                        label.charAt(0).toUpperCase() + label.slice(1),
                                },
                            ]}
                        />
                        <LogisticsEmissionCalculationTable
                            loading={loading}
                            queryParams={queryParams}
                            currentPage={currentPage}
                            pageHistoryRef={pageHistoryRef}
                            paginatedEstimates={paginatedLogisticsEstimates}
                        />
                    </>
                )}
            </WhiteBoxContainer>
        </>
    )
}

export default EmissionCalculations
